import { Injectable, OnDestroy, signal, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LeadService } from '../services/leads/lead.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';

const SOUNDRADAR_ROLES = ['inspector_viewer', 'inspector_clerk', 'inspector_admin'] as const;

@Injectable({
  providedIn: 'root',
})
export class LoginService implements OnDestroy {
  isLoggedIn = signal<boolean>(false);
  isAdmin = signal<boolean>(false);
  isAuthorized = signal<boolean>(false);

  private readonly destroy$ = new Subject<void>();
  private readonly leadService = inject(LeadService);
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  constructor() {
    // Sync state after any auth interaction completes (login, token refresh, etc.)
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.syncAuthState());

    // Also sync on explicit login/token events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.syncAuthState());

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isLoggedIn.set(false);
        this.isAuthorized.set(false);
        this.isAdmin.set(false);
      });
  }

  private syncAuthState(): void {
    const account = this.getActiveAccount();
    if (account) {
      const roles = (account.idTokenClaims?.['roles'] as string[]) ?? [];
      const authorized = SOUNDRADAR_ROLES.some((role) => roles.includes(role));
      this.isAuthorized.set(authorized);
      this.isAdmin.set(roles.includes('inspector_admin'));
      this.isLoggedIn.set(true);
    } else {
      this.isLoggedIn.set(false);
      this.isAuthorized.set(false);
      this.isAdmin.set(false);
    }
  }

  private getActiveAccount(): AccountInfo | null {
    return (
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null
    );
  }

  getUserName(): Promise<string> {
    const account = this.getActiveAccount();
    if (!account) {
      return Promise.reject(new Error('Cannot get user name, not authenticated.'));
    }
    const name = account.name ?? account.username;
    if (name) {
      return Promise.resolve(name);
    }
    return Promise.reject(new Error('Failed to retrieve user name.'));
  }

  getUserInitial(): Promise<string> {
    return this.getUserName().then(
      (name) => name.charAt(0).toUpperCase(),
      (reason) => Promise.reject(reason),
    );
  }

  async login(): Promise<void> {
    await this.msalService.loginRedirect().toPromise();
  }

  async logout(): Promise<void> {
    this.leadService.deleteLeads();
    await this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin + '/logout',
    }).toPromise();
  }

  async getUserId(): Promise<string> {
    const account = this.getActiveAccount();
    if (!account) {
      return Promise.reject(new Error('Cannot get userId, not authenticated.'));
    }
    if (account.username) {
      return account.username;
    }
    throw new Error('Failed to retrieve userId.');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
